import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function LocalPermitsLicensesPage() {
  const permitTypes = [
    {
      title: 'City of El Paso Business License',
      icon: 'ri-building-line',
      description: 'Required for all businesses operating within El Paso city limits.',
      requirements: [
        'Completed business license application',
        'Proof of business registration (Certificate of Formation)',
        'Federal EIN or Social Security Number',
        'Zoning approval for business location',
        'Payment of applicable fees'
      ],
      cost: 'Varies by business type and size',
      renewalPeriod: 'Annual',
      processingTime: '2-4 weeks',
      contact: 'City of El Paso Tax Office: (915) 212-1040'
    },
    {
      title: 'Certificate of Occupancy',
      icon: 'ri-home-gear-line',
      description: 'Certifies that your business location meets building codes and is safe for occupancy.',
      requirements: [
        'Building inspection approval',
        'Fire safety inspection (if applicable)',
        'Compliance with ADA requirements',
        'Proper zoning for intended use',
        'All construction permits closed'
      ],
      cost: '$50-$200',
      renewalPeriod: 'Not required unless major renovations',
      processingTime: '1-2 weeks after inspections',
      contact: 'Planning & Inspections: (915) 212-1553'
    },
    {
      title: 'Health Permits',
      icon: 'ri-heart-pulse-line',
      description: 'Required for food service, childcare, healthcare, and personal care businesses.',
      requirements: [
        'Health inspection of facility',
        'Food handler certifications (for food businesses)',
        'Proper equipment and sanitation systems',
        'Compliance with health codes',
        'Employee health documentation'
      ],
      cost: '$100-$500 depending on facility type',
      renewalPeriod: 'Annual',
      processingTime: '2-3 weeks',
      contact: 'El Paso Health Department: (915) 212-6520'
    },
    {
      title: 'Professional & Industry Licenses',
      icon: 'ri-briefcase-line',
      description: 'Specific licenses required for certain professions and industries.',
      requirements: [
        'Professional credentials and certifications',
        'State licensing (if applicable)',
        'Proof of insurance',
        'Background checks (for certain professions)',
        'Continuing education documentation'
      ],
      cost: 'Varies widely by profession',
      renewalPeriod: 'Varies (typically annual or biennial)',
      processingTime: '2-6 weeks',
      contact: 'Texas Department of Licensing: (512) 463-6599'
    }
  ];

  const applicationProcess = [
    {
      step: '01',
      title: 'Determine Required Permits',
      description: 'Identify which permits and licenses your specific business needs based on your industry, location, and business activities.',
      icon: 'ri-search-line'
    },
    {
      step: '02',
      title: 'Gather Documentation',
      description: 'Collect all required documents including business registration, EIN, proof of insurance, and any professional certifications.',
      icon: 'ri-folder-line'
    },
    {
      step: '03',
      title: 'Submit Applications',
      description: 'Complete and submit applications to the appropriate city, county, and state agencies. Many can be submitted online.',
      icon: 'ri-file-upload-line'
    },
    {
      step: '04',
      title: 'Schedule Inspections',
      description: 'Coordinate required inspections with city departments. Ensure your location meets all code requirements before inspection.',
      icon: 'ri-calendar-check-line'
    },
    {
      step: '05',
      title: 'Pay Fees',
      description: 'Pay all applicable permit and license fees. Keep receipts for your business records and tax purposes.',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      step: '06',
      title: 'Receive Approval',
      description: 'Once approved, receive your permits and licenses. Display them prominently at your business location as required.',
      icon: 'ri-checkbox-circle-line'
    }
  ];

  const industrySpecific = [
    {
      industry: 'Food Service & Restaurants',
      licenses: [
        'Food Service Permit',
        'Food Handler Certifications',
        'Alcohol Beverage License (if applicable)',
        'Outdoor Dining Permit (if applicable)',
        'Sign Permit'
      ]
    },
    {
      industry: 'Retail Stores',
      licenses: [
        'Sales Tax Permit',
        'Sign Permit',
        'Certificate of Occupancy',
        'Alarm Permit (if applicable)'
      ]
    },
    {
      industry: 'Construction & Contractors',
      licenses: [
        'Contractor License',
        'Building Permits (per project)',
        'Trade-specific licenses (electrical, plumbing, etc.)',
        'Vehicle permits for commercial trucks'
      ]
    },
    {
      industry: 'Healthcare & Wellness',
      licenses: [
        'Professional medical licenses',
        'Health facility permit',
        'Controlled substance registration (if applicable)',
        'HIPAA compliance documentation'
      ]
    },
    {
      industry: 'Personal Services (Salons, Spas)',
      licenses: [
        'Cosmetology establishment license',
        'Individual practitioner licenses',
        'Health permit',
        'Sign permit'
      ]
    },
    {
      industry: 'Home-Based Businesses',
      licenses: [
        'Home Occupation Permit',
        'Business license',
        'Professional licenses (if applicable)',
        'Sales tax permit (if selling products)'
      ]
    }
  ];

  const importantContacts = [
    {
      department: 'City of El Paso Tax Office',
      purpose: 'Business licenses and tax permits',
      phone: '(915) 212-1040',
      address: '221 N. Kansas St., El Paso, TX 79901',
      website: 'https://www.elpasotexas.gov/tax-office'
    },
    {
      department: 'Planning & Inspections Department',
      purpose: 'Building permits, zoning, certificates of occupancy',
      phone: '(915) 212-1553',
      address: '800 E. Overland Ave., El Paso, TX 79901',
      website: 'https://www.elpasotexas.gov/planning'
    },
    {
      department: 'El Paso Health Department',
      purpose: 'Health permits and inspections',
      phone: '(915) 212-6520',
      address: '5115 El Paso Drive, El Paso, TX 79905',
      website: 'https://www.elpasotexas.gov/public-health'
    },
    {
      department: 'Texas Alcoholic Beverage Commission',
      purpose: 'Alcohol licenses and permits',
      phone: '(512) 206-3333',
      address: 'Regional Office - El Paso',
      website: 'https://www.tabc.texas.gov'
    },
    {
      department: 'Texas Department of Licensing',
      purpose: 'Professional and occupational licenses',
      phone: '(512) 463-6599',
      address: 'Online services available',
      website: 'https://www.tdlr.texas.gov'
    }
  ];

  return (
    <>
      <SEOHead
        title="El Paso Local Permits & Licenses Guide for Businesses"
        description="Comprehensive guide to local permits and licenses required for El Paso businesses. Learn about health permits, building permits, signage permits, zoning approvals, and industry-specific licensing requirements."
        canonical="/regulations/local-permits-licenses"
        keywords="El Paso business permits, local licenses El Paso, health permit El Paso, building permit Texas, business zoning El Paso"
        ogImage="https://public.readdy.ai/ai/img_res/5753d9f758ee5950249be1ac77de8457.jpg"
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <main className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-8">
              <Link to="/regulations" className="text-[#D2691E] hover:underline text-sm font-medium">
                ← Back to Regulations
              </Link>
            </nav>

            {/* Breadcrumb Structured Data */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://elpasotexasbusiness.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Regulations",
                    "item": "https://elpasotexasbusiness.com/regulations"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Local Permits & Licenses",
                    "item": "https://elpasotexasbusiness.com/regulations/local-permits-licenses"
                  }
                ]
              })}
            </script>

            {/* Header */}
            <div className="text-center mb-16">
              <div className="w-20 h-20 bg-[#D2691E] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-shield-2-line text-4xl text-white"></i>
              </div>
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Local Permits & Licenses</h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Navigate El Paso's permit and licensing requirements with confidence. Get the permits you need to operate legally and successfully.
              </p>
            </div>

            {/* Permit Types */}
            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Required Permits & Licenses</h2>
              <p className="text-lg text-gray-600 mb-12">
                Depending on your business type and location, you may need one or more of these permits and licenses.
              </p>
              
              <div className="space-y-8">
                {permitTypes.map((permit, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-start space-x-6">
                      <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${permit.icon} text-3xl text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{permit.title}</h3>
                        <p className="text-gray-700 mb-6 leading-relaxed">{permit.description}</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                          <div>
                            <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Requirements</h4>
                            <ul className="space-y-2">
                              {permit.requirements.map((req, idx) => (
                                <li key={idx} className="flex items-start space-x-2">
                                  <i className="ri-checkbox-circle-line text-[#D2691E] text-lg flex-shrink-0 mt-0.5"></i>
                                  <span className="text-sm text-gray-700">{req}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div className="space-y-4">
                            <div className="bg-white rounded-xl p-4">
                              <div className="text-sm text-gray-600 mb-1">Cost</div>
                              <div className="text-lg font-bold text-gray-900">{permit.cost}</div>
                            </div>
                            <div className="bg-white rounded-xl p-4">
                              <div className="text-sm text-gray-600 mb-1">Renewal Period</div>
                              <div className="text-lg font-bold text-gray-900">{permit.renewalPeriod}</div>
                            </div>
                            <div className="bg-white rounded-xl p-4">
                              <div className="text-sm text-gray-600 mb-1">Processing Time</div>
                              <div className="text-lg font-bold text-gray-900">{permit.processingTime}</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <i className="ri-phone-line text-[#D2691E]"></i>
                            <span>{permit.contact}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Application Process */}
            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Application Process</h2>
              <p className="text-lg text-gray-600 mb-12">
                Follow these steps to successfully obtain your permits and licenses.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {applicationProcess.map((item, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-white">{item.step}</span>
                    </div>
                    <div className="flex items-center space-x-3 mb-3">
                      <i className={`${item.icon} text-2xl text-[#D2691E]`}></i>
                      <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Industry-Specific Requirements */}
            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Industry-Specific Requirements</h2>
              <p className="text-lg text-gray-600 mb-12">
                Different industries have unique permit and licensing requirements. Find what applies to your business.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {industrySpecific.map((item, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{item.industry}</h3>
                    <ul className="space-y-3">
                      {item.licenses.map((license, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-[#E8F5E9] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-green-600 text-sm"></i>
                          </div>
                          <span className="text-sm text-gray-700">{license}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Important Contacts */}
            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Important Contacts</h2>
              <p className="text-lg text-gray-600 mb-12">
                Key departments and agencies for permits and licenses in El Paso.
              </p>
              
              <div className="space-y-4">
                {importantContacts.map((contact, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="mb-4 md:mb-0">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{contact.department}</h3>
                        <p className="text-sm text-gray-600 mb-3">{contact.purpose}</p>
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2 text-sm text-gray-700">
                            <i className="ri-phone-line text-[#D2691E]"></i>
                            <span>{contact.phone}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-700">
                            <i className="ri-map-pin-line text-[#D2691E]"></i>
                            <span>{contact.address}</span>
                          </div>
                        </div>
                      </div>
                      <a 
                        href={contact.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-[#D2691E] hover:underline font-medium text-sm whitespace-nowrap"
                      >
                        <span>Visit Website</span>
                        <i className="ri-external-link-line"></i>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Tips Section */}
            <section className="mb-20">
              <div className="bg-gradient-to-br from-[#D2691E] to-[#A0522D] rounded-2xl p-10 text-white">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-line text-2xl text-white"></i>
                  </div>
                  <h2 className="text-3xl font-bold">Pro Tips for El Paso Businesses</h2>
                </div>
                
                <div className="space-y-4 text-white/90 leading-relaxed">
                  <p>
                    <strong className="text-white">Start Early:</strong> Begin the permit application process at least 6-8 weeks before you plan to open. Some permits require inspections and can take longer than expected.
                  </p>
                  <p>
                    <strong className="text-white">Keep Records:</strong> Maintain copies of all permits, licenses, and renewal notices. Many permits require annual renewal, and missing deadlines can result in fines.
                  </p>
                  <p>
                    <strong className="text-white">Ask Questions:</strong> City staff are there to help. Don't hesitate to call and ask questions about requirements specific to your business.
                  </p>
                  <p>
                    <strong className="text-white">Check Zoning First:</strong> Before signing a lease, verify that your business type is allowed in that zoning district. This can save you significant time and money.
                  </p>
                  <p>
                    <strong className="text-white">Consider a Professional:</strong> For complex businesses or if you're unsure about requirements, consider hiring a business consultant or attorney familiar with El Paso regulations.
                  </p>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="text-center">
              <div className="bg-[#FAFAF8] rounded-2xl p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Need Help Navigating Permits?</h2>
                <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                  We can help you understand which permits and licenses your El Paso business needs and guide you through the application process.
                </p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center space-x-2 bg-[#D2691E] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#A0522D] transition-colors whitespace-nowrap"
                >
                  <span>Get Assistance</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}